import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { STATE_STYLE, SEGMENT_STYLE, fmtDate } from "../lib/states";
import LeadDrawer from "../components/LeadDrawer";
import { Flame, Search } from "lucide-react";

function HotCard({ l, onClick }) {
  return (
    <div onClick={onClick} data-testid={`hot-card-${l.id}`}
      className="bg-white border border-red-200 ring-1 ring-red-100 rounded-xl p-4 hover:shadow-md transition-all cursor-pointer">
      <div className="flex items-start justify-between">
        <p className="font-medium text-stone-900 flex items-center gap-1.5"><Flame className="h-4 w-4 text-red-500" /> {l.name}</p>
        <span className={`text-xs px-2 py-0.5 rounded-full border ${STATE_STYLE[l.state]?.badge}`}>{l.state}</span>
      </div>
      <p className="text-xs text-stone-500 mt-1">+91 {l.phone}</p>
      <div className="flex items-center gap-2 mt-2">
        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${SEGMENT_STYLE[l.segment] || SEGMENT_STYLE.Unknown}`}>{l.segment || "Unknown"}</span>
        {l.assignee_name && <span className="text-xs text-stone-400">{l.assignee_name}</span>}
      </div>
      <p className="text-xs text-stone-400 mt-2">Flagged by {l.hot_marked_by || "—"} · {fmtDate(l.hot_marked_at)}</p>
    </div>
  );
}

export default function HotLeads() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [active, setActive] = useState(null);
  const [query, setQuery] = useState("");
  const canEdit = ["admin", "funnel_manager"].includes(user?.role);

  const load = () => {
    api.get("/leads/hot")
      .then((r) => setLeads(r.data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  };
  useEffect(() => { load(); }, []);

  const match = (l) => !query || l.name?.toLowerCase().includes(query.toLowerCase()) || l.phone?.includes(query);
  const byNewest = (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0);
  const list = leads.filter(match).sort(byNewest);

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="h-16 shrink-0 px-4 sm:px-6 flex items-center justify-between border-b border-stone-200">
        <div className="flex items-center gap-2 min-w-0">
          <Flame className="h-5 w-5 text-red-500 shrink-0" />
          <div className="min-w-0">
            <h1 className="font-heading text-xl tracking-tight text-stone-900">Hot Leads</h1>
            <p className="text-xs text-stone-500 truncate">
              {list.length} active {canEdit ? "· you can edit" : "· view-only"}
              <span className="text-stone-400"> · Lost / Junk / RNR hidden</span>
            </p>
          </div>
        </div>
        <div className="relative shrink-0">
          <Search className="h-4 w-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…" data-testid="hot-search"
            className="pl-9 pr-3 py-2 text-sm border border-stone-300 rounded-md bg-white w-36 sm:w-56 focus:outline-none focus:ring-2 focus:ring-stone-900/60" />
        </div>
      </div>

      <div className="p-4 sm:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((l) => <HotCard key={l.id} l={l} onClick={() => setActive(l)} />)}
          {list.length === 0 && (
            <p className="text-sm text-stone-400 col-span-full py-12 text-center">
              No hot leads marked yet. Callers flag promising leads as HOT and they appear here.
            </p>
          )}
        </div>
      </div>

      {active && <LeadDrawer leadId={active.id} onClose={() => setActive(null)} onSaved={load} />}
    </div>
  );
}
